// Suggest flows with AI (owner, 30 October 2026): a business gives its ideas (or none) and the AI suggests flows from
// its knowledge base, what its customers asked (the latest Insights report) and the flows it already has. It asks when
// something is unclear, changes suggestions in a back-and-forth chat, and builds only the ones the business picks, as
// switched-off drafts. `services/flows-llm`, reached through the gateway at `/v1/flows/ideas/**`.
//
// Owners and admins only: every route refuses an agent with 403 ADMIN_ROLE_REQUIRED (it spends credits and creates
// flows). Turns and builds run in the background: a POST answers 202 and the page polls `getFlowIdeaSet`.
//
// Hand-typed until the generated client is next fetched from a running stack.

import { http } from './_core/request.js';
import type { Envelope } from './_core/envelope.js';

// ── Error codes ────────────────────────────────────────────────────────────

/** 409: the AI is already thinking or building for this set. */
export const SET_BUSY = 'SET_BUSY';
/** 402: the business's credit does not cover the next turn or the build. */
export const INSUFFICIENT_CREDITS = 'INSUFFICIENT_CREDITS';
/** 413: the next turn would pass the model's working budget; start a fresh set (`startFreshFlowIdeaSet`). */
export const TOO_BIG = 'TOO_BIG';
/** 409: the plan allows no more flows (on build, when none of the picked ones fit). */
export const PLAN_LIMIT_REACHED = 'PLAN_LIMIT_REACHED';
/** 409: that model is not switched on. */
export const MODEL_NOT_AVAILABLE = 'MODEL_NOT_AVAILABLE';

// ── Settings ───────────────────────────────────────────────────────────────

export type FlowIdeaGoal =
  | 'answer_questions'
  | 'capture_leads'
  | 'bookings'
  | 'support'
  | 'sales'
  | 'enrolment'
  | 'after_hours'
  | 'feedback';
export const FLOW_IDEA_GOALS: readonly FlowIdeaGoal[] = [
  'answer_questions', 'capture_leads', 'bookings', 'support', 'sales', 'enrolment', 'after_hours', 'feedback'
];
export type FlowIdeaDetail = 'simple' | 'complete';
export type FlowIdeaChannel = 'whatsapp' | 'web' | 'messenger' | 'instagram';
export const FLOW_IDEA_CHANNELS: readonly FlowIdeaChannel[] = ['whatsapp', 'web', 'messenger', 'instagram'];
export type FlowIdeaTone = 'friendly' | 'formal' | 'playful';

export interface FlowIdeaSettings {
  /** What the AI uses. */
  use_knowledge: boolean;
  /** Document ids to use; empty means every document. */
  documents: string[];
  use_insights: boolean;
  use_flows: boolean;
  use_apis: boolean;
  use_bookings: boolean;
  use_tickets: boolean;
  /** Goal and detail. */
  goals: FlowIdeaGoal[];
  /** How many suggestions to write: 3 to 20. */
  count: number;
  detail: FlowIdeaDetail;
  /** Channels, language and tone. */
  channels: FlowIdeaChannel[];
  /** The language the flows speak to customers: a code such as en, af, zu, xh, st. */
  language: string;
  tone: FlowIdeaTone;
  /** The chatbot the flows belong to; null is the default chatbot. */
  bot_id: string | null;
  /** Safety and hand-over, on by default. */
  offer_person: boolean;
  office_hours: boolean;
  consent: boolean;
  /** Catalogue codes from `FlowIdeaOptions.models`. */
  suggest_model: string;
  build_model: string;
}

// ── Options ────────────────────────────────────────────────────────────────

export interface FlowIdeaModel {
  code: string;
  name: string;
  description: string | null;
  is_default: boolean;
  /** The model's context window, and the working budget the page keeps under (the lower of half and 60 000). */
  context_tokens: number;
  budget_tokens: number;
  /** About what one suggestion turn costs with this model, for this business's knowledge base; null when unpriced. */
  turn_minor: number | null;
  /** About what building one flow costs with this model. */
  build_minor: number | null;
}

export interface FlowIdeaOptions {
  models: FlowIdeaModel[];
  currency: string;
  available_minor: number | null;
  /** The plan's flows: null `allowed` is unlimited; `room` is how many more may be created. */
  plan: { used: number; allowed: number | null; room: number | null };
  /** The latest finished Insights report, when there is one. */
  insights: { report_id: string; from: string; to: string; finished_at: string } | null;
  bots: { id: string; name: string; is_default: boolean }[];
  documents: { id: string; name: string }[];
  flows: number;
  apis: number;
  bookings_on: boolean;
  tickets_on: boolean;
  /** Settings filled from the chatbot's persona, for a new set. */
  defaults: FlowIdeaSettings;
}

// ── A set ──────────────────────────────────────────────────────────────────

export type FlowIdeaSetStatus = 'idle' | 'thinking' | 'building' | 'done' | 'failed' | 'cancelled';

/**
 * One line of the AI panel's live activity. `key` is translated by the page:
 * - `knowledge` (count: documents), `insights` (date), `flows` (count), `apis` (count),
 * - `writing` (from, to, total: suggestions being written), `folding` (the earlier chat being summarised),
 * - `building` (name, n, total), `checking` (the built flow being checked).
 */
export interface FlowIdeaActivity {
  /** `looseEnds` (2 November 2026): the build's last stage, `params.count` loose ends in the business's switched-on flows. */
  key: 'knowledge' | 'insights' | 'flows' | 'apis' | 'writing' | 'folding' | 'building' | 'checking' | 'looseEnds';
  params: Record<string, string | number>;
  state: 'done' | 'now' | 'later';
}

/** How full the AI's memory is for this set: what the next turn would send against the model's working budget. */
export interface FlowIdeaMemory {
  used_tokens: number;
  budget_tokens: number;
  context_tokens: number;
  /** green below 60%, amber to 85%, red above. */
  band: 'green' | 'amber' | 'red';
  /** The earlier chat was folded into a note to stay accurate. */
  folded: boolean;
  summary: string | null;
  /** The next turn would pass the budget: offer `startFreshFlowIdeaSet`. */
  too_big: boolean;
}

export interface FlowIdeaQuestion {
  text: string;
  options: string[];
}

export interface FlowIdeaMessage {
  n: number;
  role: 'user' | 'assistant' | 'system';
  /** For the assistant, allow-listed HTML (render with ReplyHtml); plain text for the person and the system. */
  text: string;
  questions: FlowIdeaQuestion[];
  /** Suggestion numbers the person asked to build: the page picks them and asks to confirm. */
  build: number[];
  tokens_in: number | null;
  tokens_out: number | null;
  spent_minor: number | null;
  at: string;
}

export type FlowIdeaSourceKind = 'document' | 'correction' | 'flow' | 'insight' | 'idea' | 'api';

export interface FlowIdeaProposal {
  /** A new flow, or a change to an existing one (built as a switched-off copy "(improved)"; the original is untouched). */
  kind: 'new' | 'improve';
  improves: { flow_id: string; flow_name: string } | null;
  name: string;
  purpose: string;
  description: string;
  outcome: string;
  starts: {
    type: 'keywords' | 'intent' | 'entry';
    keywords: string[];
    intent: string | null;
    entry: 'first_contact' | 'greeting' | 'default' | 'post_resolution' | null;
  };
  /** 3 to 8 short lines. */
  steps: string[];
  /** For an improvement, the steps as they are now. */
  steps_before: string[];
  why: string;
  /** The sources the suggestion rests on (checked in code: an unknown one is not listed). */
  sources: { label: string; kind: FlowIdeaSourceKind; name: string }[];
  uses: { documents: string[]; apis: string[]; flows: { id: string; name: string }[]; bookings: boolean; tickets: boolean };
  needs: { kind: 'document' | 'template' | 'number' | 'api' | 'info'; what: string }[];
  priority: 'high' | 'medium' | 'low';
  size: FlowIdeaDetail;
  goal: FlowIdeaGoal | null;
}

/** Something the code found to check before building. */
export interface FlowIdeaFlag {
  code: 'not_grounded' | 'fact_not_found' | 'unknown_document' | 'unknown_api' | 'unknown_flow' | 'keyword_clash' | 'overlaps' | 'entry_taken';
  /** The thing itself: a fee, a keyword, a document name. */
  detail: string;
  flow_id: string | null;
  flow_name: string | null;
}

export type FlowIdeaItemStatus = 'proposed' | 'removed' | 'queued' | 'building' | 'created' | 'failed' | 'skipped';

export interface FlowIdeaItem {
  n: number;
  status: FlowIdeaItemStatus;
  proposal: FlowIdeaProposal;
  flags: FlowIdeaFlag[];
  /** False when the suggestion is not grounded: the page leaves it unticked. */
  pick_by_default: boolean;
  /** About what building it costs with the set's building model. */
  build_minor: number | null;
  build_tokens: number | null;
  note: string | null;
  /** The flow it became (switched off), with what Build with AI found. */
  flow_id: string | null;
  flow_name: string | null;
  problems: string[];
  needs: { kind: string; what: string }[];
  clashes: { keyword: string; flow_id: string; flow_name: string }[];
  /** Why it was not created (plan limit, too big, could not build). */
  error: string | null;
  /** For an improvement once built: the flow it improves, and whether the copy is now the one switched on. */
  original_flow_id: string | null;
  switched: boolean;
  /** Whether the flow it improves still exists (2 November 2026). */
  original_exists: boolean;
  /** When the copy replaced the old version, which was deleted; null until then. */
  replaced_at: string | null;
  /** The old version can be put back (switched off) with {@link restoreOldFlowVersion}: within 30 days, once. */
  can_restore: boolean;
  /** The flow the old version was put back as. */
  restored_flow_id: string | null;
  /**
   * Another built copy of the same old version already replaced it (SJM built two copies of one Welcome flow), so this
   * one has nothing left to replace; replacing all skips it.
   */
  superseded: boolean;
}

export interface FlowIdeaSet {
  id: string;
  status: FlowIdeaSetStatus;
  settings: FlowIdeaSettings;
  /** What the AI writes to the person in: en or af (the console's language). */
  language: 'en' | 'af';
  activity: FlowIdeaActivity[];
  memory: FlowIdeaMemory;
  build: { total: number; done: number; current: string | null } | null;
  spent_minor: number;
  currency: string;
  tokens_in: number;
  tokens_out: number;
  messages: FlowIdeaMessage[];
  items: FlowIdeaItem[];
  error: string | null;
  /** A set started fresh from this one, and the one this was started from. */
  fresh_from: string | null;
  created_at: string;
  updated_at: string;
}

export interface FlowIdeaSetSummary {
  id: string;
  status: FlowIdeaSetStatus;
  /** The first words the person wrote, or null when they asked from the knowledge base. */
  title: string | null;
  suggestions: number;
  created: number;
  spent_minor: number;
  currency: string;
  created_at: string;
  updated_at: string;
}

export interface FlowIdeaEstimate {
  tokens_in: number;
  tokens_out: number;
  minor: number | null;
  currency: string;
  affordable: boolean;
  /** False when it would pass the model's working budget. */
  fits: boolean;
  band: FlowIdeaMemory['band'];
}

// ── Calls ──────────────────────────────────────────────────────────────────

export function getFlowIdeaOptions(): Promise<Envelope<FlowIdeaOptions>> {
  return http.get<Envelope<FlowIdeaOptions>>('/v1/flows/ideas/options');
}

/**
 * What a message would cost and whether it fits: `chars` is the length of what the person is about to send. With no
 * `set_id`, the first turn of a new set with `settings`.
 */
export function estimateFlowIdeaTurn(opts: { set_id?: string; model: string; chars: number }): Promise<Envelope<FlowIdeaEstimate>> {
  return http.get<Envelope<FlowIdeaEstimate>>('/v1/flows/ideas/estimate', { query: { set_id: opts.set_id, model: opts.model, chars: opts.chars } });
}

/** Start a set with its settings and the first message (the ideas, or empty to suggest from the knowledge base). 202. */
export function createFlowIdeaSet(req: { settings: FlowIdeaSettings; text: string; language?: 'en' | 'af'; insight_gaps?: string }): Promise<Envelope<FlowIdeaSet>> {
  return http.post<Envelope<FlowIdeaSet>>('/v1/flows/ideas/sets', { body: req });
}

export function listFlowIdeaSets(limit?: number): Promise<Envelope<FlowIdeaSetSummary[]>> {
  return http.get<Envelope<FlowIdeaSetSummary[]>>('/v1/flows/ideas/sets', { query: { limit } });
}

export function getFlowIdeaSet(id: string): Promise<Envelope<FlowIdeaSet>> {
  return http.get<Envelope<FlowIdeaSet>>(`/v1/flows/ideas/sets/${encodeURIComponent(id)}`);
}

/** Change the set's settings; the AI's view of the business is gathered again on the next turn. */
export function saveFlowIdeaSettings(id: string, settings: FlowIdeaSettings): Promise<Envelope<FlowIdeaSet>> {
  return http.put<Envelope<FlowIdeaSet>>(`/v1/flows/ideas/sets/${encodeURIComponent(id)}/settings`, { body: settings });
}

/** Send the person's message (an answer, a change, "suggest more"). 202; 409 SET_BUSY; 402; 413 TOO_BIG. */
export function sendFlowIdeaMessage(id: string, text: string): Promise<Envelope<FlowIdeaSet>> {
  return http.post<Envelope<FlowIdeaSet>>(`/v1/flows/ideas/sets/${encodeURIComponent(id)}/messages`, { body: { text } });
}

/** Build the picked suggestions as switched-off flows. 202; 402; 409 SET_BUSY or PLAN_LIMIT_REACHED. */
export function buildFlowIdeas(id: string, req: { items: { n: number; note?: string }[]; model: string }): Promise<Envelope<FlowIdeaSet>> {
  return http.post<Envelope<FlowIdeaSet>>(`/v1/flows/ideas/sets/${encodeURIComponent(id)}/build`, { body: req });
}

/** Stop the turn or the build under way; flows already built stay. */
export function cancelFlowIdeaSet(id: string): Promise<Envelope<FlowIdeaSet>> {
  return http.post<Envelope<FlowIdeaSet>>(`/v1/flows/ideas/sets/${encodeURIComponent(id)}/cancel`);
}

/** A new set with this one's settings, its kept suggestions and the summary of the chat, when this one grew too big. 201. */
export function startFreshFlowIdeaSet(id: string): Promise<Envelope<FlowIdeaSet>> {
  return http.post<Envelope<FlowIdeaSet>>(`/v1/flows/ideas/sets/${encodeURIComponent(id)}/fresh`);
}

/**
 * For a built improvement: switch the improved copy on and the original off (both are kept). Since 2 November 2026 every
 * Run another flow step that opened the original opens the copy too.
 */
export function switchToImprovedFlow(id: string, n: number): Promise<Envelope<FlowIdeaSet>> {
  return http.post<Envelope<FlowIdeaSet>>(`/v1/flows/ideas/sets/${encodeURIComponent(id)}/items/${n}/switch`);
}

/**
 * Replace the old version with this built improvement (2 November 2026): every Run another flow step that opened it
 * opens the copy, its saved Web API keys and its runs move to the copy, the copy takes its name and is switched on, and
 * the old version is deleted (it can be put back for 30 days). Fine when the old version is already gone. 409
 * `NOT_AN_IMPROVEMENT`, `FLOW_GONE` or `SET_BUSY`.
 */
export function replaceWithImprovedFlow(id: string, n: number): Promise<Envelope<FlowIdeaSet>> {
  return http.post<Envelope<FlowIdeaSet>>(`/v1/flows/ideas/sets/${encodeURIComponent(id)}/items/${n}/replace`);
}

/** Replace every old version with its built improvement not yet replaced, or those numbered in `items`. */
export function replaceAllWithImprovedFlows(id: string, items?: number[]): Promise<Envelope<FlowIdeaSet>> {
  return http.post<Envelope<FlowIdeaSet>>(`/v1/flows/ideas/sets/${encodeURIComponent(id)}/replace`, { body: { items: items ?? [] } });
}

/** Put a replaced old version back as a switched-off flow named "(old version)"; links stay with the copy. Within 30 days. */
export function restoreOldFlowVersion(id: string, n: number): Promise<Envelope<FlowIdeaSet>> {
  return http.post<Envelope<FlowIdeaSet>>(`/v1/flows/ideas/sets/${encodeURIComponent(id)}/items/${n}/restore`);
}
